import "./styles.less";

import React, { FC } from "react";
import { Skeleton, Typography } from "antd";

import { BathroomsTag } from "./BathroomsTag/web";
import { BedroomsTag } from "./BedroomsTag/web";
import { GuestsTag } from "./GuestsTag/web";
import { M2Tag } from "./M2Tag/web";
import { RoomsTag } from "./RoomsTag/web";
import { useFilters } from "../../Filters/Filters.hook";
import { useTypologyTag } from "./TypologyTag.hook";

export interface TypologyTagProps {
	propertyType: string;
	operationType: string;
	bedrooms?: number | number[];
	bathrooms?: number | number[];
	rooms?: number | number[];
	m2?: number;
	hectares?: number;
	guests?: number;
	loading: boolean;
	showIcons?: boolean;
	className?: string;
	isEspecial?: Boolean;
	isProject?: boolean;
}

export const TypologyTag: FC<TypologyTagProps> = ({
	propertyType,
	operationType,
	bedrooms,
	rooms,
	bathrooms,
	m2,
	hectares,
	guests,
	loading,
	showIcons = false,
	className = "",
	isEspecial,
	isProject = false,
}) => {
	const { showBathrooms, showBedrooms, showM2, showRooms, showGuests } = useTypologyTag({
		propertyType,
		operationType,
		isProject,
		bedrooms,
		bathrooms,
		rooms
	});
	const { filters } = useFilters();

	if (loading) return <TypologyTagSkeleton className={className} />;

	const separador = !showIcons ? <Typography.Text>·</Typography.Text> : null;

	const ROOMS_PROPERTY_TYPES = [4, 5, 9, 19];
	const usesRooms = ROOMS_PROPERTY_TYPES.includes(Number(propertyType));

	const getMinBedroom = bedrooms => {
		if (Array.isArray(bedrooms)) {
			if (filters.bedrooms && filters.bedrooms[0]) {
				for (const rooms of bedrooms) {
					if (rooms >= filters.bedrooms[0]) {
						return rooms;
					}
				}
			} else {
				return bedrooms[0];
			}
		}

		return bedrooms;
	};

	const getMinRoom = rooms => {
		if (Array.isArray(rooms)) {
			if (filters.rooms && filters.rooms[0]) {
				for (const room of rooms) {
					if (room >= filters.rooms[0]) {
						return room;
					}
				}
			} else {
				return rooms[0];
			}
		}

		return rooms;
	};

	const getMinBathooms = bathrooms => {
		if (Array.isArray(bathrooms)) {
			if (filters.bathrooms && filters.bathrooms[0]) {
				for (const bathroom of bathrooms) {
					if (bathroom >= filters.bathrooms[0]) {
						return bathroom;
					}
				}
			} else {
				return bathrooms[0];
			}
		}

		return bathrooms;
	};
	const minBedrooms = isProject || !usesRooms ? getMinBedroom(bedrooms) : undefined;
	const minRooms = isProject || usesRooms ? getMinRoom(rooms) : undefined;
	const minBathrooms = getMinBathooms(bathrooms);

	let shouldShowBedroomsTag: boolean;
	let shouldShowRoomsTag: boolean;
	let shouldShowBathroomsTag: boolean;

	if (isProject) {
		// minBedrooms/minRooms ya excluyen las unidades tipo Lote (ver PropertyDetails/web.tsx),
		// y reflejan si el proyecto tiene unidades con bedrooms y/o con rooms, sin importar
		// el propertyType de la unidad más barata (showBedrooms/showRooms del hook no sirve
		// acá porque solo mira esa unidad). Si hay bedrooms y rooms se prioriza bedrooms
		// (el menor, solo se muestra uno).
		shouldShowBedroomsTag = minBedrooms != null && Number(minBedrooms) >= 0;
		shouldShowRoomsTag = !shouldShowBedroomsTag && minRooms != null && Number(minRooms) >= 0;
		// baños se muestra siempre que llegue un valor >= 0, sin importar el tipo de propiedad
		shouldShowBathroomsTag = minBathrooms != null && Number(minBathrooms) >= 0;
	} else {
		const hideZeroRooms = Number(minRooms) === 0;
		shouldShowBedroomsTag = showBedrooms && Number(minBedrooms) >= 0;
		shouldShowRoomsTag = showRooms && !hideZeroRooms && Number(minRooms) >= 0;
		shouldShowBathroomsTag = showBathrooms && minBathrooms > 0;
	}

	return (
		<div
			className={
				"property-typology-tag" + " " + className + (showIcons ? " show-icons" : "")
			}>
			{shouldShowBedroomsTag && (
				<BedroomsTag isEspecial={isEspecial} bedrooms={minBedrooms} icon={showIcons} />
			)}

			{shouldShowRoomsTag && shouldShowBedroomsTag && separador}
			{shouldShowRoomsTag && (
				<RoomsTag isEspecial={isEspecial} rooms={minRooms} icon={showIcons} />
			)}

			{shouldShowBathroomsTag && (shouldShowBedroomsTag || shouldShowRoomsTag) && separador}
			{shouldShowBathroomsTag && (
				<BathroomsTag isEspecial={isEspecial} bathrooms={minBathrooms} icon={showIcons} />
			)}

			{showM2 && m2 > 0 && (shouldShowBedroomsTag || shouldShowRoomsTag || shouldShowBathroomsTag) && separador}
			{showM2 && m2 > 0 && (
				<M2Tag
					isEspecial={isEspecial}
					m2={m2}
					hectares={hectares}
					icon={showIcons}
					propertyType={Number(propertyType)}
				/>
			)}

			{showGuests && (shouldShowBedroomsTag || shouldShowRoomsTag || shouldShowBathroomsTag || showM2) && separador}
			{showGuests && <GuestsTag isEspecial={isEspecial} guests={guests} icon={showIcons} />}
		</div>
	);
};

export const TypologyTagSkeleton = ({ className }) => {
	return (
		<div className={"property-typology-tag" + " " + className}>
			<TagSkeleton />
			<TagSkeleton />
			<TagSkeleton />
		</div>
	);
};

export const TagSkeleton = () => {
	return <Skeleton.Button size={"small"} active />;
};
